import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Card,
	CardContent,
	Autocomplete,
	Select,
	MenuItem,
	FormControl,
	FormHelperText,
	TextField,
	Chip,
	Alert,
	Stack,
	CircularProgress,
	IconButton,
	List,
	ListItemButton,
	Tooltip,
	Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
	ExtractionLogRow,
	ExtractionLogCell,
	MessageEvidence,
	ResolveCellPayload,
	WorkCategory,
	resolveExtractionLog,
	markMessageHandled,
	fetchMessageEvidenceBlob,
	uploadMessageEvidence,
} from '../../services/messageQueue.service';
import { getFullUsersList } from '../../services/user.service';
import { fetchAllProjects, getProjectTypeAssociation } from '../../services/project.service';
import { fetchAllPrecincts } from '../../services/precinct.service';
import { fetchAllBlocks } from '../../services/block.service';
import { fetchAllTypes, fetchAllSubTypesByType, fetchAllSubValuesBySubType } from '../../services/type.service';
import {
	CATEGORY_DEFAULTS,
	getWorkItemFieldIssues,
	getWorkItemReviewIssues,
	isWorkItemReady,
	WorkItemIssueField,
} from '../../utils/workDoneReview';

interface Worker {
	id: number;
	name: string;
}

interface ProjectOpt {
	id: number;
	name: string;
	isHip: boolean;
}

interface TypeOpt {
	id: number;
	name: string;
}

interface PrecinctOpt {
	id: number;
	name: string;
	location?: string | null;
}

interface BlockOpt {
	id: number;
	number: string | number;
	precinctId: number;
	levelFrom: number;
	levelTo: number;
	unitFrom: number;
	unitTo: number;
	unitNumberPattern: 'continuous' | 'odd' | 'even';
}

interface SubTypeOpt {
	id: number;
	name: string;
}

interface SubValueOpt {
	id: number;
	value: string;
}

interface ProjectToTypeRow {
	typeId: number;
	clientId: number;
	clientName?: string;
	typeName?: string;
}

interface CellFormState {
	cellId: string;
	rawLine: string | null;
	flags: string[];
	userId: number | null;
	helperUserIds: number[];
	projectId: number | null;
	clientId: number | null;
	clientName: string | null; // derived display label
	precinctId: number | null;
	blockId: number | null;
	blockLevelFrom: number | null;
	blockLevelTo: number | null;
	blockUnitFrom: number | null;
	blockUnitTo: number | null;
	blockUnitNumberPattern: 'continuous' | 'odd' | 'even' | null;
	typeId: number | null;
	subTypeId: number | null;
	subTypeValueId: number | null;
	level: number | null;
	unit: number | null;
	statusType: 0 | 1;
	quantity: number | null;
	quantityUnit: string;
	performedAt: string;
	workCategory: WorkCategory;
	isClientBillable: boolean;
	isWorkerCreditable: boolean;
	reportedLocationText: string;
	includeAsWork: boolean;
}

interface CellFormGroup {
	key: string;
	indices: number[];
}

/**
 * The API intentionally keeps one cell per physical unit. The review UI does
 * not need to repeat all catalogue fields for those cells, though. Cells with
 * the same work, people and level are presented as one editable line with a
 * compact unit list; they are expanded back to individual cells on submit.
 */
const getCellFormGroupKey = (form: CellFormState): string =>
	JSON.stringify([
		form.userId,
		[...form.helperUserIds].sort((a, b) => a - b),
		form.projectId,
		form.clientId,
		form.precinctId,
		form.blockId,
		form.typeId,
		form.subTypeId,
		form.subTypeValueId,
		form.level,
		form.statusType,
		form.quantity,
		form.quantityUnit,
		form.performedAt,
		form.workCategory,
		form.isClientBillable,
		form.isWorkerCreditable,
		form.reportedLocationText,
		form.includeAsWork,
	]);

const groupReviewForms = (forms: CellFormState[]): CellFormGroup[] =>
	forms.reduce<CellFormGroup[]>((groups, form, index) => {
		const key = getCellFormGroupKey(form);
		const existing = groups.find((group) => group.key === key);
		if (existing) existing.indices.push(index);
		else groups.push({ key, indices: [index] });
		return groups;
	}, []);

const formatUnitList = (forms: CellFormState[], indices: number[]): string =>
	indices
		.map((index) => forms[index]?.unit)
		.filter((unit): unit is number => unit !== null && unit !== undefined)
		.join(', ');

const parseUnitList = (value: string): number[] => {
	const units: number[] = [];
	for (const token of value
		.replace(/#/g, '')
		.split(/[\s,]+/)
		.filter(Boolean)) {
		const range = token.match(/^(\d+)\s*-\s*(\d+)$/);
		if (range) {
			const start = Number(range[1]);
			const end = Number(range[2]);
			if (end >= start && end - start <= 200) {
				for (let unit = start; unit <= end; unit += 1) units.push(unit);
			}
			continue;
		}
		if (/^\d+$/.test(token)) units.push(Number(token));
	}
	return Array.from(new Set(units));
};

const toLocalDateTime = (value: string | null | undefined): string => {
	if (!value) return '';
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

const buildInitialForm = (cell: ExtractionLogCell, fallbackPerformedAt: string): CellFormState => {
	const statusType = (cell.resolved.statusType.value ?? 0) as 0 | 1;
	const workCategory =
		cell.workCategory ?? cell.resolved.workCategory?.value ?? (statusType === 1 ? 'free_redo' : 'normal');
	const categoryDefaults = CATEGORY_DEFAULTS[workCategory];
	const primaryParticipant = cell.participants?.find((participant) => participant.role === 'primary');
	const helperUserIds =
		cell.participants
			?.filter((participant) => participant.role === 'helper')
			.map((participant) => participant.userId) ??
		cell.coWorkers?.map((worker) => worker.id) ??
		[];

	return {
		cellId: cell.cellId,
		rawLine: cell.rawLine,
		flags: cell.flags ?? [],
		userId: primaryParticipant?.userId ?? cell.resolved.userId.value,
		helperUserIds,
		projectId: cell.resolved.projectId.value,
		clientId: cell.resolved.clientId.value,
		// A label is display metadata, not a resolution. Never surface a stale
		// client label while the actual client field is unresolved.
		clientName: cell.resolved.clientId.value == null ? null : cell.labels.clientName,
		precinctId: cell.resolved.precinctId.value,
		blockId: cell.resolved.blockId.value,
		blockLevelFrom: cell.labels.blockLevelFrom,
		blockLevelTo: cell.labels.blockLevelTo,
		blockUnitFrom: cell.labels.blockUnitFrom,
		blockUnitTo: cell.labels.blockUnitTo,
		blockUnitNumberPattern: cell.labels.blockUnitNumberPattern,
		typeId: cell.resolved.typeId.value,
		subTypeId: cell.resolved.subTypeId.value,
		subTypeValueId: cell.resolved.subTypeValueId.value,
		level: cell.resolved.level.value,
		unit: cell.resolved.unit.value,
		statusType,
		quantity: cell.quantity ?? cell.resolved.quantity?.value ?? 1,
		quantityUnit: cell.quantityUnit ?? cell.resolved.quantityUnit?.value ?? 'unit',
		performedAt: toLocalDateTime(cell.performedAt ?? cell.resolved.performedAt?.value ?? fallbackPerformedAt),
		workCategory,
		isClientBillable: categoryDefaults.isClientBillable,
		isWorkerCreditable:
			cell.isWorkerCreditable ?? cell.resolved.isWorkerCreditable?.value ?? categoryDefaults.isWorkerCreditable,
		reportedLocationText: cell.reportedLocationText ?? cell.resolved.reportedLocationText?.value ?? '',
		includeAsWork: cell.reviewStatus !== 'rejected',
	};
};

const compactControlSx = {
	'& .MuiOutlinedInput-root': {
		minHeight: 42,
		borderRadius: 2,
		backgroundColor: '#f8fafc',
		transition: 'background-color 150ms ease, box-shadow 150ms ease',
		'& fieldset': { borderColor: '#dce3eb' },
		'&:hover': {
			backgroundColor: '#fff',
			'& fieldset': { borderColor: '#9eacba' },
		},
		'&.Mui-focused': {
			backgroundColor: '#fff',
			boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.10)',
		},
	},
	'& .MuiOutlinedInput-input': { py: 1.05 },
	'& .MuiSelect-select': { py: '10px !important' },
	'& .MuiAutocomplete-inputRoot': { py: '2px !important' },
	'& .MuiFormHelperText-root': { mt: 0.35, mx: 0.25, lineHeight: 1.2 },
} as const;

const FieldLabel: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
	<Typography
		component='label'
		htmlFor={htmlFor}
		variant='caption'
		sx={{ display: 'block', mb: 0.45, color: '#475569', fontWeight: 650, letterSpacing: '0.01em' }}>
		{children}
	</Typography>
);

const placeholder = (label: string) => (
	<Box component='span' sx={{ color: '#94a3b8' }}>
		{label}
	</Box>
);

/** Distinguishes WHY a thumbnail failed so the reviewer knows what to do,
 * instead of one unhelpful "Image unavailable" for every cause. */
const evidenceErrorLabel = (error: unknown): string => {
	const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
	if (status === 401 || status === 403) return 'Session expired — sign in again';
	if (status === 404) return 'Photo not found';
	if (status !== undefined && status >= 500) return 'Server error — retry shortly';
	return 'Image unavailable';
};

const EvidenceThumbnail: React.FC<{
	evidence: MessageEvidence;
	onOpen: (url: string) => void;
}> = ({ evidence, onOpen }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [errorLabel, setErrorLabel] = useState<string | null>(null);

	useEffect(() => {
		let active = true;
		let objectUrl: string | null = null;
		setErrorLabel(null);
		fetchMessageEvidenceBlob(evidence.id)
			.then((blob) => {
				if (!active) return;
				objectUrl = URL.createObjectURL(blob);
				setUrl(objectUrl);
			})
			.catch((error) => active && setErrorLabel(evidenceErrorLabel(error)));
		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [evidence.id]);

	if (errorLabel) return <Chip size='small' color='error' variant='outlined' label={errorLabel} />;
	return (
		<Box
			component='button'
			type='button'
			disabled={!url}
			onClick={() => url && onOpen(url)}
			sx={{
				p: 0,
				width: 52,
				height: 52,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1,
				overflow: 'hidden',
				cursor: url ? 'pointer' : 'wait',
				backgroundColor: 'grey.100',
			}}>
			{url ? (
				<Box
					component='img'
					src={url}
					alt={evidence.fileName ?? 'Work evidence'}
					sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
				/>
			) : (
				<CircularProgress size={22} />
			)}
		</Box>
	);
};

const buildEmptyForm = (performedAt: string): CellFormState => ({
	cellId: `admin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	rawLine: null,
	flags: ['admin-created-draft'],
	userId: null,
	helperUserIds: [],
	projectId: null,
	clientId: null,
	clientName: null,
	precinctId: null,
	blockId: null,
	blockLevelFrom: null,
	blockLevelTo: null,
	blockUnitFrom: null,
	blockUnitTo: null,
	blockUnitNumberPattern: null,
	typeId: null,
	subTypeId: null,
	subTypeValueId: null,
	level: null,
	unit: null,
	statusType: 0,
	quantity: 1,
	quantityUnit: 'unit',
	performedAt: toLocalDateTime(performedAt),
	workCategory: 'normal',
	isClientBillable: true,
	isWorkerCreditable: true,
	reportedLocationText: '',
	includeAsWork: true,
});

interface Props {
	show: boolean;
	row: ExtractionLogRow | null;
	readOnly?: boolean;
	onClose: () => void;
	onSuccess: () => void;
	onEvidenceChanged?: () => void;
}

export const ResolveExtractionLogDialog: React.FC<Props> = ({
	show,
	row,
	readOnly = false,
	onClose,
	onSuccess,
	onEvidenceChanged,
}) => {
	// Top-level catalog (loaded once on open)
	const [workers, setWorkers] = useState<Worker[]>([]);
	const [projects, setProjects] = useState<ProjectOpt[]>([]);
	const [types, setTypes] = useState<TypeOpt[]>([]);

	// Per-project lazy caches
	const [precinctsByProject, setPrecinctsByProject] = useState<Record<number, PrecinctOpt[]>>({});
	const [ptByProject, setPtByProject] = useState<Record<number, ProjectToTypeRow[]>>({});
	// Blocks are precinct-scoped (the API endpoint is precinct/getAll)
	const [blocksByPrecinct, setBlocksByPrecinct] = useState<Record<number, BlockOpt[]>>({});

	// Per-type / per-subtype lazy caches
	const [subTypesByType, setSubTypesByType] = useState<Record<number, SubTypeOpt[]>>({});
	const [valuesBySubType, setValuesBySubType] = useState<Record<number, SubValueOpt[]>>({});

	const [forms, setForms] = useState<CellFormState[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [treatAsWorkDone, setTreatAsWorkDone] = useState(false);
	const [previewEvidenceUrl, setPreviewEvidenceUrl] = useState<string | null>(null);
	const [evidenceItems, setEvidenceItems] = useState<MessageEvidence[]>([]);
	const [uploadingEvidence, setUploadingEvidence] = useState(false);
	const [showOriginalMessage, setShowOriginalMessage] = useState(false);
	const [activeGroupIndex, setActiveGroupIndex] = useState(0);

	useEffect(() => {
		setEvidenceItems(row?.evidence ?? []);
		setPreviewEvidenceUrl(null);
		setShowOriginalMessage(false);
		setActiveGroupIndex(0);
	}, [row?.id]);

	// Top-level loaders --------------------------------------------------------
	useEffect(() => {
		if (!show || readOnly) return;
		(async () => {
			try {
				const [wRes, pRes, tRes] = await Promise.all([
					getFullUsersList('worker'),
					fetchAllProjects('project/all'),
					fetchAllTypes('type/all'),
				]);
				if (wRes?.success) {
					const ws = (wRes.data?.rows ?? []).map((u: any) => ({ id: u.id, name: u.name }));
					setWorkers(ws);
				}
				if (pRes?.data?.success) {
					const ps = (pRes.data.data ?? []).map((p: any) => ({ id: p.id, name: p.name, isHip: !!p.isHip }));
					setProjects(ps);
					setForms((current) =>
						current.map((form) => {
							const project = ps.find((candidate: ProjectOpt) => candidate.id === form.projectId);
							return {
								...form,
								isClientBillable: form.workCategory === 'normal' && project?.isHip === true,
							};
						})
					);
				}
				if (tRes?.data?.success) {
					const ts = (tRes.data.data ?? []).map((t: any) => ({ id: t.id, name: t.name }));
					setTypes(ts);
				}
			} catch (e: any) {
				toast.error('Could not load catalog: ' + (e?.message ?? 'unknown'));
			}
		})();
	}, [show, readOnly, row?.rawMessage]);

	// Per-project lazy loader (precincts + project-to-type) -------------------
	const loadProjectData = useCallback(
		async (projectId: number): Promise<ProjectToTypeRow[]> => {
			if (precinctsByProject[projectId] && ptByProject[projectId]) {
				return ptByProject[projectId];
			}
			try {
				const [precRes, ptRes] = await Promise.all([
					fetchAllPrecincts('precinct/getAll?projectId=' + projectId),
					getProjectTypeAssociation('project/typeAssociation/getAll', projectId),
				]);
				if (precRes?.data?.success) {
					const ps: PrecinctOpt[] = (precRes.data.data ?? []).map((p: any) => ({
						id: p.id,
						name: p.name,
						location: p.location ?? null,
					}));
					setPrecinctsByProject((prev) => ({ ...prev, [projectId]: ps }));
					if (ps.length === 0) {
						setForms((current) =>
							current.map((form) =>
								form.projectId === projectId
									? {
											...form,
											precinctId: null,
											blockId: null,
											blockLevelFrom: null,
											blockLevelTo: null,
											blockUnitFrom: null,
											blockUnitTo: null,
											blockUnitNumberPattern: null,
											level: null,
											unit: null,
									  }
									: form
							)
						);
					}
				}
				let loadedAssociations: ProjectToTypeRow[] = [];
				if (ptRes?.data?.success) {
					loadedAssociations = (ptRes.data.data ?? [])
						.map((row: any) => ({
							typeId: Number(row.typeId),
							clientId: Number(row.clientId),
							clientName: row.clientName,
							typeName: row.typeName,
						}))
						.filter(
							(association: ProjectToTypeRow, index: number, rows: ProjectToTypeRow[]) =>
								rows.findIndex(
									(candidate) => candidate.typeId === association.typeId && candidate.clientId === association.clientId
								) === index
						);
					setPtByProject((prev) => ({ ...prev, [projectId]: loadedAssociations }));
				}
				return loadedAssociations;
			} catch (e: any) {
				toast.error('Could not load project data: ' + (e?.message ?? 'unknown'));
				return [];
			}
		},
		[precinctsByProject, ptByProject]
	);

	// Per-precinct block loader
	const loadBlocksForPrecinct = useCallback(
		async (precinctId: number) => {
			if (blocksByPrecinct[precinctId]) return;
			try {
				const res = await fetchAllBlocks('block/getAll?precinctId=' + precinctId);
				if (res?.data?.success) {
					const bs: BlockOpt[] = (res.data.data ?? []).map((b: any) => ({
						id: b.id,
						number: b.number,
						precinctId: b.precinctId,
						levelFrom: Number(b.levelFrom ?? 1),
						levelTo: Number(b.levelTo),
						unitFrom: Number(b.unitFrom),
						unitTo: Number(b.unitTo),
						unitNumberPattern: b.unitNumberPattern ?? 'continuous',
					}));
					setBlocksByPrecinct((prev) => ({ ...prev, [precinctId]: bs }));
				}
			} catch (e: any) {
				toast.error('Could not load blocks: ' + (e?.message ?? 'unknown'));
			}
		},
		[blocksByPrecinct]
	);

	// Per-type / per-subtype lazy loaders --------------------------------------
	const loadSubTypes = useCallback(
		async (typeId: number) => {
			if (subTypesByType[typeId]) return;
			try {
				const res = await fetchAllSubTypesByType('type/subtype/name/getall?typeId=' + typeId);
				if (res?.data?.success) {
					const sts: SubTypeOpt[] = (res.data.data ?? []).map((s: any) => ({
						id: s.id,
						name: s.name,
					}));
					setSubTypesByType((prev) => ({ ...prev, [typeId]: sts }));
				}
			} catch (e: any) {
				toast.error('Could not load sub-types: ' + (e?.message ?? 'unknown'));
			}
		},
		[subTypesByType]
	);

	const loadSubValues = useCallback(
		async (subTypeId: number) => {
			if (valuesBySubType[subTypeId]) return;
			try {
				const res = await fetchAllSubValuesBySubType('type/subtype/value/getall?subtypeId=' + subTypeId);
				if (res?.data?.success) {
					const vs: SubValueOpt[] = (res.data.data ?? []).map((v: any) => ({
						id: v.id,
						value: v.value,
					}));
					setValuesBySubType((prev) => ({ ...prev, [subTypeId]: vs }));
				}
			} catch (e: any) {
				toast.error('Could not load sub-values: ' + (e?.message ?? 'unknown'));
			}
		},
		[valuesBySubType]
	);

	// Reset form ONLY when the row changes (dialog opens with new data).
	// Lazy loaders are NOT in deps — they recreate every cache update and
	// would wipe admin's in-progress edits.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!row) {
			setForms([]);
			setTreatAsWorkDone(false);
			return;
		}
		setTreatAsWorkDone(false);
		const initial = row.cells.map((cell) => buildInitialForm(cell, row.messageTimestamp ?? row.createdAt));
		setForms(initial);

		// Processed messages use their persisted labels in a compact summary.
		// They do not need live catalog lookups or edit-time validation.
		if (readOnly) return;

		// Trigger lazy loads for whatever the AI/Tier-1 already picked
		const projectIds = new Set<number>();
		const precinctIds = new Set<number>();
		const typeIds = new Set<number>();
		const subTypeIds = new Set<number>();
		for (const f of initial) {
			if (f.projectId) projectIds.add(f.projectId);
			if (f.precinctId) precinctIds.add(f.precinctId);
			if (f.typeId) typeIds.add(f.typeId);
			if (f.subTypeId) subTypeIds.add(f.subTypeId);
		}
		projectIds.forEach((id) => loadProjectData(id));
		precinctIds.forEach((id) => loadBlocksForPrecinct(id));
		typeIds.forEach((id) => loadSubTypes(id));
		subTypeIds.forEach((id) => loadSubValues(id));
	}, [row?.id, readOnly]);

	const updateCellForms = (indices: number[], patch: Partial<CellFormState>) => {
		const selected = new Set(indices);
		setForms((prev) => prev.map((form, index) => (selected.has(index) ? { ...form, ...patch } : form)));
	};

	const updateGroupUnits = (indices: number[], value: string) => {
		const units = parseUnitList(value);
		if (units.length === 0) {
			updateCellForms(indices, { unit: null });
			return;
		}
		setForms((current) => {
			const selected = new Set(indices);
			const firstIndex = indices[0];
			const templates = indices.map((index) => current[index]).filter(Boolean);
			if (!templates.length) return current;
			const replacements = units.map((unit, unitIndex) => {
				const template = templates[unitIndex] ?? templates[0];
				return {
					...template,
					cellId:
						unitIndex < templates.length
							? template.cellId
							: `admin-${Date.now()}-${unit}-${Math.random().toString(36).slice(2, 8)}`,
					unit,
					// One structured apartment unit is one physical unit of work.
					// The grouped Quantity shown to the admin is derived from the
					// number of these records, so each persisted record remains 1.
					quantity: 1,
					quantityUnit: 'unit',
				};
			});
			const next: CellFormState[] = [];
			current.forEach((form, index) => {
				if (index === firstIndex) next.push(...replacements);
				if (!selected.has(index)) next.push(form);
			});
			return next;
		});
	};

	const addEmptyItem = () => {
		if (!row) return;
		setActiveGroupIndex(groupedForms.length);
		setForms((prev) => [...prev, buildEmptyForm(row.messageTimestamp ?? row.createdAt)]);
	};

	const onGroupProjectChange = async (indices: number[], projectId: number | null) => {
		const selectedProject = projects.find((project) => project.id === projectId);
		updateCellForms(indices, {
			projectId,
			precinctId: null,
			blockId: null,
			blockLevelFrom: null,
			blockLevelTo: null,
			blockUnitFrom: null,
			blockUnitTo: null,
			blockUnitNumberPattern: null,
			clientId: null,
			clientName: null,
			isClientBillable: selectedProject?.isHip === true && forms[indices[0]]?.workCategory === 'normal',
		});
		if (!projectId) return;
		const associations = await loadProjectData(projectId);
		const selected = new Set(indices);
		setForms((current) =>
			current.map((form, index) => {
				if (!selected.has(index) || form.typeId == null) return form;
				const matches = associations.filter((association) => association.typeId === form.typeId);
				return matches.length === 1
					? { ...form, clientId: matches[0].clientId, clientName: matches[0].clientName ?? null }
					: form;
			})
		);
	};

	const onGroupPrecinctChange = async (indices: number[], precinctId: number | null) => {
		updateCellForms(indices, {
			precinctId,
			blockId: null,
			blockLevelFrom: null,
			blockLevelTo: null,
			blockUnitFrom: null,
			blockUnitTo: null,
			blockUnitNumberPattern: null,
		});
		if (precinctId) await loadBlocksForPrecinct(precinctId);
	};

	const onGroupBlockChange = (indices: number[], blockId: number | null) => {
		const precinctId = forms[indices[0]]?.precinctId;
		const block = precinctId
			? (blocksByPrecinct[precinctId] ?? []).find((candidate) => candidate.id === blockId)
			: undefined;
		updateCellForms(indices, {
			blockId,
			blockLevelFrom: block ? Number(block.levelFrom) : null,
			blockLevelTo: block ? Number(block.levelTo) : null,
			blockUnitFrom: block ? Number(block.unitFrom) : null,
			blockUnitTo: block ? Number(block.unitTo) : null,
			blockUnitNumberPattern: block?.unitNumberPattern ?? null,
		});
	};

	const onGroupTypeChange = async (indices: number[], typeId: number | null) => {
		const projectId = forms[indices[0]]?.projectId ?? null;
		updateCellForms(indices, { typeId, subTypeId: null, subTypeValueId: null, clientId: null, clientName: null });
		if (!typeId) return;
		await loadSubTypes(typeId);
		const associations = projectId ? await loadProjectData(projectId) : [];
		const matches = associations.filter((association) => association.typeId === typeId);
		if (matches.length === 1) {
			updateCellForms(indices, { clientId: matches[0].clientId, clientName: matches[0].clientName ?? null });
		}
	};

	const onGroupSubTypeChange = async (indices: number[], subTypeId: number | null) => {
		updateCellForms(indices, { subTypeId, subTypeValueId: null });
		if (subTypeId) await loadSubValues(subTypeId);
	};

	const onGroupWorkersChange = (indices: number[], selectedUserIds: number[]) => {
		const uniqueUserIds = Array.from(new Set(selectedUserIds));
		const currentPrimary = forms[indices[0]]?.userId;
		const primaryUserId =
			currentPrimary !== null && currentPrimary !== undefined && uniqueUserIds.includes(currentPrimary)
				? currentPrimary
				: uniqueUserIds[0] ?? null;
		updateCellForms(indices, {
			userId: primaryUserId,
			helperUserIds: uniqueUserIds.filter((userId) => userId !== primaryUserId),
		});
	};

	const onGroupWorkCategoryChange = (indices: number[], workCategory: WorkCategory) => {
		const defaults = CATEGORY_DEFAULTS[workCategory];
		const isHip = !!projects.find((project) => project.id === forms[indices[0]]?.projectId)?.isHip;
		updateCellForms(indices, {
			workCategory,
			statusType: workCategory === 'free_redo' || workCategory === 'rectify' ? 1 : 0,
			isClientBillable: isHip && defaults.isClientBillable,
			isWorkerCreditable: defaults.isWorkerCreditable,
		});
	};

	const groupedForms = useMemo(() => groupReviewForms(forms), [forms]);
	const requiresStructuredLocation = (form: CellFormState): boolean => {
		if (form.projectId == null) return true;
		const configuredPrecincts = precinctsByProject[form.projectId];
		// Stay conservative until project configuration finishes loading.
		return configuredPrecincts === undefined || configuredPrecincts.length > 0;
	};
	const getFormReviewIssues = (form: CellFormState): string[] =>
		getWorkItemReviewIssues(form, { requireStructuredLocation: requiresStructuredLocation(form) });
	const isFormComplete = (form: CellFormState): boolean =>
		isWorkItemReady(form, { requireStructuredLocation: requiresStructuredLocation(form) });
	useEffect(() => {
		if (groupedForms.length === 0) setActiveGroupIndex(0);
		else if (activeGroupIndex >= groupedForms.length) setActiveGroupIndex(groupedForms.length - 1);
	}, [activeGroupIndex, groupedForms.length]);

	// Checked cells become Work Done records. Unchecked cells are intentionally
	// rejected and the message is closed after the checked work is resolved.
	const includedForms = forms.filter((form) => form.includeAsWork);
	const completeCount = includedForms.filter(isFormComplete).length;
	const incompleteCount = includedForms.length - completeCount;
	const rejectedCount = forms.length - includedForms.length;

	const handleSubmit = async () => {
		if (!row) return;
		if (includedForms.length === 0) {
			await handleMarkReviewed();
			return;
		}
		if (incompleteCount > 0) {
			toast.warning('Resolve every checked work item or uncheck an item that should not be created.');
			return;
		}
		const payload: ResolveCellPayload[] = [];
		for (let i = 0; i < forms.length; i++) {
			const f = forms[i];
			if (!f.includeAsWork) continue;
			if (!isFormComplete(f)) continue;
			const keepStructuredLocation = requiresStructuredLocation(f);
			payload.push({
				cellId: f.cellId,
				userId: f.userId!,
				projectId: f.projectId!,
				clientId: f.clientId!,
				precinctId: keepStructuredLocation ? f.precinctId : null,
				blockId: keepStructuredLocation ? f.blockId : null,
				typeId: f.typeId!,
				subTypeId: f.subTypeId!,
				subTypeValueId: f.subTypeValueId!,
				level: keepStructuredLocation ? f.level : null,
				unit: keepStructuredLocation ? f.unit : null,
				statusType: f.statusType,
				quantity: f.quantity!,
				quantityUnit: f.quantityUnit,
				performedAt: dayjs(f.performedAt).toISOString(),
				workCategory: f.workCategory,
				isClientBillable: f.isClientBillable,
				isWorkerCreditable: f.isWorkerCreditable,
				helperUserIds: f.helperUserIds,
				reportedLocationText: f.reportedLocationText.trim() || null,
			});
		}
		if (payload.length === 0) {
			toast.warning('No complete cells to save — fill in the missing fields first.');
			return;
		}
		try {
			setSubmitting(true);
			const res = await resolveExtractionLog(row.id, payload);
			if (res?.success) {
				const created = res.data?.created?.length ?? 0;
				const skipped = res.data?.skipped?.length ?? 0;
				if (rejectedCount > 0 && res.data?.logStatus !== 'done') {
					const handled = await markMessageHandled(row.id);
					if (!handled?.success) throw new Error(handled?.message ?? 'Could not finish reviewing rejected items');
				}
				toast.success(
					`Approved ${created} work item${created === 1 ? '' : 's'}` +
						(skipped > 0 ? ` (${skipped} already existed)` : '') +
						(rejectedCount > 0 ? ` · rejected ${rejectedCount}` : '')
				);
				onSuccess();
			} else {
				toast.error(res?.message ?? 'Resolve failed');
			}
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? e?.message ?? 'Resolve failed');
		} finally {
			setSubmitting(false);
		}
	};

	const handleMarkReviewed = async () => {
		if (!row) return;
		try {
			setSubmitting(true);
			const res = await markMessageHandled(row.id);
			if (res?.success) {
				toast.success(res.message ?? 'Marked reviewed.');
				onSuccess();
			} else {
				toast.error(res?.message ?? 'Could not mark reviewed');
			}
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? e?.message ?? 'Could not mark reviewed');
		} finally {
			setSubmitting(false);
		}
	};

	const handleEvidenceUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const image = event.target.files?.[0];
		event.target.value = '';
		if (!row || !image) return;
		try {
			setUploadingEvidence(true);
			const result = await uploadMessageEvidence(row.id, image);
			if (!result?.success || !result.data) throw new Error(result?.message ?? 'Upload failed');
			setEvidenceItems((current) => {
				const withoutDuplicate = current.filter((evidence) => evidence.id !== result.data.id);
				return [...withoutDuplicate, result.data];
			});
			toast.success('Photo uploaded.');
			onEvidenceChanged?.();
		} catch (error: any) {
			toast.error(error?.response?.data?.message ?? error?.message ?? 'Could not upload photo.');
		} finally {
			setUploadingEvidence(false);
		}
	};

	if (!row) return null;

	const createdIds = row.createdWorkDoneIds ?? [];
	const isWorkDone = row.messageType === 'work-done' || treatAsWorkDone;
	const hasPossibleBillableDuplicate =
		row.flags?.includes('possible_duplicate_billable_work') ||
		forms.some((form) => form.flags.includes('possible_duplicate_billable_work'));
	const duplicateReviewDetails = Array.from(
		new Set([...(row.crossCheckIssues ?? []), ...row.cells.flatMap((cell) => cell.crossCheckIssues ?? [])])
	);
	const handleTreatAsWorkDone = () => {
		setTreatAsWorkDone(true);
		if (forms.length === 0) {
			setForms([buildEmptyForm(row.messageTimestamp ?? row.createdAt)]);
		}
	};

	return (
		<Dialog
			open={show}
			onClose={onClose}
			fullScreen
			PaperProps={{ sx: { bgcolor: '#f4f7fb', backgroundImage: 'none' } }}>
			<DialogTitle
				sx={{
					minHeight: 68,
					px: { xs: 2, md: 3 },
					py: 1.25,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					bgcolor: '#fff',
					borderBottom: '1px solid #e2e8f0',
				}}>
				<Box>
					<Typography variant='h6' sx={{ fontWeight: 750, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
						{readOnly ? 'Completed work' : 'Review work'}
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						{dayjs(row.messageTimestamp ?? row.createdAt).format('DD MMM YYYY, HH:mm')} ·{' '}
						{row.senderName ?? row.fromPhone ?? 'Unknown sender'}
					</Typography>
				</Box>
				<IconButton onClick={onClose} aria-label='Close'>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, overflowY: 'auto' }}>
				<Box sx={{ width: '100%', maxWidth: 1600, mx: 'auto' }}>
					{readOnly && (
						<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1.25 }}>
							<Chip
								label={
									createdIds.length > 0
										? `${createdIds.length} approved ${createdIds.length === 1 ? 'entry' : 'entries'}`
										: 'Reviewed — no work created'
								}
								color={createdIds.length > 0 ? 'success' : 'default'}
								size='small'
								sx={{ fontWeight: 650 }}
							/>
							<Button
								size='small'
								onClick={() => setShowOriginalMessage((current) => !current)}
								sx={{ textTransform: 'none' }}>
								{showOriginalMessage ? 'Hide original message' : 'Show original message'}
							</Button>
						</Stack>
					)}

					{(!readOnly || showOriginalMessage) && (
						<Box
							sx={{
								mb: 1.5,
								px: { xs: 1.5, sm: 2 },
								py: 1.25,
								backgroundColor: '#eef6ff',
								border: '1px solid #d9e9fb',
								borderRadius: 3,
							}}>
							<Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5}>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant='caption' sx={{ color: '#315f96', fontWeight: 700 }}>
										Original message
									</Typography>
									<Typography
										variant='body2'
										sx={{
											mt: 0.2,
											whiteSpace: 'pre-wrap',
											lineHeight: 1.45,
											color: '#24364b',
										}}>
										{row.rawMessage}
									</Typography>
								</Box>
								{!readOnly && (
									<Stack direction='row' alignItems='center' spacing={0.75} sx={{ flexShrink: 0 }}>
										<Typography variant='caption' sx={{ fontWeight: 700, color: '#475569' }}>
											Photos
										</Typography>
										{evidenceItems.length > 0 ? (
											<Stack direction='row' spacing={0.5}>
												{evidenceItems.map((evidence) => (
													<EvidenceThumbnail key={evidence.id} evidence={evidence} onOpen={setPreviewEvidenceUrl} />
												))}
											</Stack>
										) : (
											<Typography variant='caption' color='text.secondary'>
												No photo attached
											</Typography>
										)}
										<Button
											component='label'
											size='small'
											variant='text'
											startIcon={uploadingEvidence ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
											disabled={uploadingEvidence}
											sx={{ textTransform: 'none', borderRadius: 2 }}>
											{evidenceItems.length > 0 ? 'Add another photo' : 'Add photo'}
											<input
												hidden
												type='file'
												accept='image/jpeg,image/png,image/webp'
												onChange={handleEvidenceUpload}
											/>
										</Button>
									</Stack>
								)}
							</Stack>
						</Box>
					)}

					{!readOnly && hasPossibleBillableDuplicate && (
						<Alert severity='warning' sx={{ mb: 2 }}>
							<strong>Possible duplicate billable work.</strong> Work with the same activity and exact location already
							exists. Uncheck This is work to ignore a duplicate, change the work category to Free redo or Rectify for
							analytics-only repeat work, or approve Normal only when this is genuinely additional billable work.
							{duplicateReviewDetails.length > 0 && (
								<Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
									{duplicateReviewDetails.join(' · ')}
								</Typography>
							)}
						</Alert>
					)}

					{readOnly && evidenceItems.length > 0 && (
						<Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.25, minHeight: 34 }}>
							<Typography variant='caption' sx={{ fontWeight: 700, color: '#475569' }}>
								Photos
							</Typography>
							{evidenceItems.length > 0 ? (
								<Stack direction='row' spacing={0.75} sx={{ flexWrap: 'wrap' }}>
									{evidenceItems.map((evidence) => (
										<EvidenceThumbnail key={evidence.id} evidence={evidence} onOpen={setPreviewEvidenceUrl} />
									))}
								</Stack>
							) : (
								<Typography variant='caption' color='text.secondary'>
									No photo attached
								</Typography>
							)}
						</Stack>
					)}
					<Dialog open={previewEvidenceUrl !== null} onClose={() => setPreviewEvidenceUrl(null)} maxWidth='md'>
						<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							Evidence
							<IconButton onClick={() => setPreviewEvidenceUrl(null)} aria-label='Close evidence'>
								<CloseIcon />
							</IconButton>
						</DialogTitle>
						{previewEvidenceUrl && (
							<Box
								component='img'
								src={previewEvidenceUrl}
								alt='Work evidence'
								sx={{ maxWidth: '100%', maxHeight: '75vh' }}
							/>
						)}
					</Dialog>

					{!isWorkDone && !readOnly && (
						<Alert
							severity='info'
							sx={{ mb: 2 }}
							action={
								<Button color='inherit' size='small' onClick={handleTreatAsWorkDone}>
									Treat as work done
								</Button>
							}>
							<strong>Classified as {row.messageType.replace('-', ' ')}.</strong> This message isn{"'"}t work-done and
							won
							{"'"}t create any WorkDone rows unless you explicitly treat it as work done, add valid items, and approve
							them. Otherwise, mark it reviewed to clear it from the queue.
						</Alert>
					)}

					{isWorkDone && forms.length === 0 && (
						<Alert
							severity='warning'
							action={
								!readOnly ? (
									<Button color='inherit' size='small' startIcon={<AddIcon />} onClick={addEmptyItem}>
										Add work item
									</Button>
								) : undefined
							}>
							No work locations were found. Add one from the message or finish the review without creating work.
						</Alert>
					)}

					{isWorkDone && readOnly && row.cells.length > 0 && <ProcessedWorkSummary cells={row.cells} />}

					{isWorkDone && !readOnly && forms.length > 0 && (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
								gap: 1.5,
								alignItems: 'start',
							}}>
							<Box
								component='nav'
								aria-label='Work items'
								sx={{
									p: 1.25,
									minWidth: 0,
									bgcolor: '#fff',
									border: '1px solid #e2e8f0',
									borderRadius: 3,
									boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
								}}>
								<Box sx={{ px: 0.5 }}>
									<Typography variant='subtitle2' sx={{ fontWeight: 750 }}>
										{groupedForms.length} work {groupedForms.length === 1 ? 'item' : 'items'}
									</Typography>
									<Typography variant='caption' color='text.secondary'>
										Review one item at a time
									</Typography>
								</Box>
								<List disablePadding sx={{ mt: 1.25 }}>
									{groupedForms.map((group, groupIndex) => {
										const form = forms[group.indices[0]];
										const title = types.find((type) => type.id === form.typeId)?.name ?? `Work item ${groupIndex + 1}`;
										const included = group.indices.some((index) => forms[index].includeAsWork);
										const complete = included && group.indices.every((index) => isFormComplete(forms[index]));
										return (
											<ListItemButton
												key={group.key}
												selected={groupIndex === activeGroupIndex}
												onClick={() => setActiveGroupIndex(groupIndex)}
												sx={{
													width: '100%',
													mb: 0.75,
													px: 1.25,
													py: 0.7,
													borderRadius: 2,
													border: '1px solid',
													borderColor: groupIndex === activeGroupIndex ? '#9fc0e9' : '#dce3eb',
													bgcolor: '#fff',
													'&.Mui-selected': { bgcolor: '#eef5ff' },
													'&.Mui-selected:hover': { bgcolor: '#e7f1ff' },
												}}>
												<Checkbox
													size='small'
													checked={included}
													onClick={(event) => event.stopPropagation()}
													onChange={(event) => updateCellForms(group.indices, { includeAsWork: event.target.checked })}
													inputProps={{ 'aria-label': `Mark work item ${groupIndex + 1} as work` }}
													sx={{ p: 0.25, mr: 0.75, alignSelf: 'flex-start' }}
												/>
												<Box sx={{ minWidth: 0, flex: 1 }}>
													<Typography variant='body2' sx={{ fontWeight: 650 }} noWrap>
														{title}
													</Typography>
													<Typography variant='caption' color={complete ? 'text.secondary' : 'warning.main'} noWrap>
														{!included
															? 'Not work'
															: complete
															? `${group.indices.length} ${group.indices.length === 1 ? 'unit' : 'units'}`
															: 'Needs attention'}
													</Typography>
												</Box>
											</ListItemButton>
										);
									})}
								</List>
								<Button
									fullWidth
									size='small'
									variant='outlined'
									startIcon={<AddIcon />}
									onClick={addEmptyItem}
									sx={{ mt: 0.25, borderRadius: 2, textTransform: 'none', bgcolor: '#fff' }}>
									Add work item
								</Button>
							</Box>
							<Box
								sx={{
									p: { xs: 1.5, md: 2.25 },
									minWidth: 0,
									bgcolor: '#fff',
									border: '1px solid #e2e8f0',
									borderRadius: 3,
									boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
								}}>
								{groupedForms.map((group, groupIndex) => {
									const idx = group.indices[0];
									const form = forms[idx];
									const included = group.indices.some((index) => forms[index].includeAsWork);
									const complete = included && group.indices.every((index) => isFormComplete(forms[index]));
									const reviewIssues = Array.from(
										new Set(group.indices.flatMap((index) => getFormReviewIssues(forms[index])))
									);
									const fieldIssues = group.indices.flatMap((index) =>
										getWorkItemFieldIssues(forms[index], {
											requireStructuredLocation: requiresStructuredLocation(forms[index]),
										})
									);
									const invalidFields: Set<WorkItemIssueField> = new Set(fieldIssues.flatMap((issue) => issue.fields));
									// The specific reason(s) a field is flagged, for its helper text —
									// not just that SOMETHING is wrong with it. Prefers each issue's short
									// fieldHint (when set) over its full message, which stays in the bottom
									// "Please correct" summary line unchanged.
									const fieldMessage = (field: WorkItemIssueField): string | undefined => {
										const messages = Array.from(
											new Set(
												fieldIssues
													.filter((issue) => issue.fields.includes(field))
													.map((issue) => issue.fieldHint ?? issue.message)
											)
										);
										return messages.length > 0 ? messages.join(' · ') : undefined;
									};
									const blocksForPrecinct = form.precinctId ? blocksByPrecinct[form.precinctId] ?? [] : [];
									const selectedBlock = blocksForPrecinct.find((block) => block.id === form.blockId);
									const levelFrom = form.blockLevelFrom ?? selectedBlock?.levelFrom ?? 1;
									const levelTo = form.blockLevelTo ?? selectedBlock?.levelTo ?? null;
									const levelInvalid =
										form.level !== null && (form.level < levelFrom || (levelTo !== null && form.level > levelTo));
									const unitInvalid = group.indices.some((index) => {
										const unit = forms[index]?.unit;
										return (
											unit !== null &&
											unit !== undefined &&
											((form.blockUnitFrom !== null && unit < form.blockUnitFrom) ||
												(form.blockUnitTo !== null && unit > form.blockUnitTo) ||
												(form.blockUnitNumberPattern === 'odd' && unit % 2 === 0) ||
												(form.blockUnitNumberPattern === 'even' && unit % 2 !== 0))
										);
									});
									const projectPrecincts = form.projectId ? precinctsByProject[form.projectId] ?? [] : [];
									const projectHasNoPrecincts =
										form.projectId != null &&
										precinctsByProject[form.projectId] !== undefined &&
										projectPrecincts.length === 0;
									const selectedProject = projects.find((project) => project.id === form.projectId);
									const isNonHipProject = selectedProject?.isHip === false;
									const quantityIsEditable = isNonHipProject || projectHasNoPrecincts;
									const typeSubTypes = form.typeId ? subTypesByType[form.typeId] ?? [] : [];
									const subTypeValues = form.subTypeId ? valuesBySubType[form.subTypeId] ?? [] : [];
									const projectTypes = form.projectId ? ptByProject[form.projectId] : undefined;
									const clientAssociations = (projectTypes ?? [])
										.filter((association) => association.typeId === form.typeId)
										.filter(
											(association, index, matches) =>
												matches.findIndex((candidate) => candidate.clientId === association.clientId) === index
										);
									const projectTypeMissing =
										form.projectId != null &&
										form.typeId != null &&
										projectTypes !== undefined &&
										clientAssociations.length === 0;
									const selectedWorkers = [form.userId, ...form.helperUserIds].filter(
										(userId): userId is number => userId !== null
									);
									const selectedWorkerOptions = selectedWorkers.map(
										(id) =>
											workers.find((worker) => worker.id === id) ?? {
												id,
												name: form.userId === id ? row.senderName ?? `Worker ${id}` : `Worker ${id}`,
											}
									);
									const structuredUnitCount = group.indices.filter(
										(index) => forms[index]?.unit !== null && forms[index]?.unit !== undefined
									).length;
									const displayedQuantity =
										!quantityIsEditable && structuredUnitCount > 0 ? structuredUnitCount : form.quantity ?? 1;
									return (
										<Box
											key={group.indices.map((index) => forms[index].cellId).join('|')}
											sx={{
												display: groupIndex === activeGroupIndex ? 'block' : 'none',
											}}>
											<Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.5 }}>
												<Box sx={{ minWidth: 0 }}>
													<Typography variant='caption' sx={{ color: '#315f96', fontWeight: 700 }}>
														AI interpreted this as
													</Typography>
													<Typography variant='h6' sx={{ fontWeight: 750, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
														{types.find((type) => type.id === form.typeId)?.name ?? `Work item ${groupIndex + 1}`}
													</Typography>
													{groupedForms.length > 1 && (
														<Typography variant='caption' color='text.secondary'>
															Item {groupIndex + 1} of {groupedForms.length}
														</Typography>
													)}
												</Box>
												{group.indices.length > 1 && (
													<Chip label={`${group.indices.length} units`} size='small' variant='outlined' />
												)}
												<Chip
													sx={{ ml: 'auto' }}
													label={!included ? 'Not work' : complete ? 'Ready to record' : 'Needs attention'}
													size='small'
													color={!included ? 'default' : complete ? 'success' : 'warning'}
													variant='outlined'
												/>
												{groupedForms.length > 1 && (
													<Stack direction='row' spacing={0.25}>
														<Tooltip title='Previous item'>
															<span>
																<IconButton
																	size='small'
																	disabled={groupIndex === 0}
																	onClick={() => setActiveGroupIndex(groupIndex - 1)}>
																	<NavigateBeforeIcon fontSize='small' />
																</IconButton>
															</span>
														</Tooltip>
														<Tooltip title='Next item'>
															<span>
																<IconButton
																	size='small'
																	disabled={groupIndex === groupedForms.length - 1}
																	onClick={() => setActiveGroupIndex(groupIndex + 1)}>
																	<NavigateNextIcon fontSize='small' />
																</IconButton>
															</span>
														</Tooltip>
													</Stack>
												)}
												{group.indices.every((index) => forms[index].flags.includes('admin-created-draft')) && (
													<IconButton
														size='small'
														color='error'
														onClick={() =>
															setForms((current) => current.filter((_, index) => !group.indices.includes(index)))
														}
														aria-label={`Remove work group ${groupIndex + 1}`}>
														<DeleteOutlineIcon fontSize='small' />
													</IconButton>
												)}
											</Stack>

											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
													columnGap: 1.5,
													rowGap: 1.25,
													opacity: included ? 1 : 0.55,
												}}>
												<Box sx={{ gridColumn: { md: 'span 6' }, ...compactControlSx }}>
													<FieldLabel htmlFor={`workers-${form.cellId}`}>Workers</FieldLabel>
													<Autocomplete
														multiple
														filterSelectedOptions
														size='small'
														options={workers}
														value={selectedWorkerOptions}
														getOptionLabel={(option) => option.name}
														isOptionEqualToValue={(option, value) => option.id === value.id}
														onChange={(_, value) =>
															onGroupWorkersChange(
																group.indices,
																value.map((worker) => worker.id)
															)
														}
														renderInput={(params) => (
															<TextField
																{...params}
																id={`workers-${form.cellId}`}
																placeholder='Search workers'
																error={invalidFields.has('userId')}
																helperText={fieldMessage('userId')}
															/>
														)}
													/>
												</Box>

												<Box sx={{ gridColumn: { md: 'span 6' }, ...compactControlSx }}>
													<FieldLabel>Project</FieldLabel>
													<FormControl size='small' fullWidth error={invalidFields.has('projectId')}>
														<Select
															displayEmpty
															inputProps={{ 'aria-label': 'Project' }}
															value={form.projectId ?? ''}
															renderValue={(value) =>
																projects.find((project) => project.id === Number(value))?.name ??
																(value ? String(value) : placeholder('Select project'))
															}
															onChange={(event) =>
																onGroupProjectChange(
																	group.indices,
																	event.target.value === '' ? null : Number(event.target.value)
																)
															}>
															{form.projectId != null && !projects.some((project) => project.id === form.projectId) && (
																<MenuItem value={form.projectId} sx={{ display: 'none' }}>
																	{form.projectId}
																</MenuItem>
															)}
															{projects.map((project) => (
																<MenuItem key={project.id} value={project.id}>
																	{project.name}
																</MenuItem>
															))}
														</Select>
														{fieldMessage('projectId') && <FormHelperText>{fieldMessage('projectId')}</FormHelperText>}
													</FormControl>
												</Box>

												{clientAssociations.length > 1 && (
													<Box sx={{ gridColumn: { md: 'span 12' }, ...compactControlSx }}>
														<FieldLabel>Client</FieldLabel>
														<FormControl size='small' fullWidth error={invalidFields.has('clientId')}>
															<Select
																displayEmpty
																inputProps={{ 'aria-label': 'Client' }}
																value={form.clientId ?? ''}
																renderValue={(value) =>
																	clientAssociations.find((candidate) => candidate.clientId === Number(value))
																		?.clientName ?? placeholder('Select client')
																}
																onChange={(event) => {
																	const association = clientAssociations.find(
																		(candidate) => candidate.clientId === Number(event.target.value)
																	);
																	updateCellForms(group.indices, {
																		clientId: association?.clientId ?? null,
																		clientName: association?.clientName ?? null,
																	});
																}}>
																{clientAssociations.map((association) => (
																	<MenuItem key={association.clientId} value={association.clientId}>
																		{association.clientName ?? `Client ${association.clientId}`}
																	</MenuItem>
																))}
															</Select>
															{fieldMessage('clientId') && <FormHelperText>{fieldMessage('clientId')}</FormHelperText>}
														</FormControl>
													</Box>
												)}

												<Box sx={{ gridColumn: { md: 'span 3' }, ...compactControlSx }}>
													<FieldLabel>Type</FieldLabel>
													<FormControl size='small' fullWidth error={invalidFields.has('typeId')}>
														<Select
															displayEmpty
															inputProps={{ 'aria-label': 'Type' }}
															value={form.typeId ?? ''}
															renderValue={(value) =>
																types.find((type) => type.id === Number(value))?.name ??
																(value ? String(value) : placeholder('Select type'))
															}
															onChange={(event) =>
																onGroupTypeChange(
																	group.indices,
																	event.target.value === '' ? null : Number(event.target.value)
																)
															}>
															{form.typeId != null && !types.some((type) => type.id === form.typeId) && (
																<MenuItem value={form.typeId} sx={{ display: 'none' }}>
																	{form.typeId}
																</MenuItem>
															)}
															{types.map((type) => (
																<MenuItem key={type.id} value={type.id}>
																	{type.name}
																</MenuItem>
															))}
														</Select>
														{fieldMessage('typeId') && <FormHelperText>{fieldMessage('typeId')}</FormHelperText>}
													</FormControl>
												</Box>

												<Box sx={{ gridColumn: { md: 'span 3' }, ...compactControlSx }}>
													<FieldLabel>Sub Type</FieldLabel>
													<FormControl
														size='small'
														fullWidth
														disabled={form.typeId == null}
														error={invalidFields.has('subTypeId')}>
														<Select
															displayEmpty
															inputProps={{ 'aria-label': 'Sub Type' }}
															value={form.subTypeId ?? ''}
															renderValue={(value) =>
																typeSubTypes.find((subType) => subType.id === Number(value))?.name ??
																(value ? String(value) : placeholder('Select sub type'))
															}
															onChange={(event) =>
																onGroupSubTypeChange(
																	group.indices,
																	event.target.value === '' ? null : Number(event.target.value)
																)
															}>
															{form.subTypeId != null &&
																!typeSubTypes.some((subType) => subType.id === form.subTypeId) && (
																	<MenuItem value={form.subTypeId} sx={{ display: 'none' }}>
																		{form.subTypeId}
																	</MenuItem>
																)}
															{typeSubTypes.map((subType) => (
																<MenuItem key={subType.id} value={subType.id}>
																	{subType.name}
																</MenuItem>
															))}
														</Select>
														{fieldMessage('subTypeId') && <FormHelperText>{fieldMessage('subTypeId')}</FormHelperText>}
													</FormControl>
												</Box>

												<Box sx={{ gridColumn: { md: 'span 3' }, ...compactControlSx }}>
													<FieldLabel>Sub Type Value</FieldLabel>
													<FormControl
														size='small'
														fullWidth
														disabled={form.subTypeId == null}
														error={invalidFields.has('subTypeValueId')}>
														<Select
															displayEmpty
															inputProps={{ 'aria-label': 'Sub Type Value' }}
															value={form.subTypeValueId ?? ''}
															renderValue={(value) =>
																subTypeValues.find((candidate) => candidate.id === Number(value))?.value ??
																(value ? String(value) : placeholder('Select value'))
															}
															onChange={(event) =>
																updateCellForms(group.indices, {
																	subTypeValueId: event.target.value === '' ? null : Number(event.target.value),
																})
															}>
															{form.subTypeValueId != null &&
																!subTypeValues.some((candidate) => candidate.id === form.subTypeValueId) && (
																	<MenuItem value={form.subTypeValueId} sx={{ display: 'none' }}>
																		{form.subTypeValueId}
																	</MenuItem>
																)}
															{subTypeValues.map((value) => (
																<MenuItem key={value.id} value={value.id}>
																	{value.value}
																</MenuItem>
															))}
														</Select>
														{fieldMessage('subTypeValueId') && (
															<FormHelperText>{fieldMessage('subTypeValueId')}</FormHelperText>
														)}
													</FormControl>
												</Box>

												<Box sx={{ gridColumn: { md: 'span 3' }, ...compactControlSx }}>
													<FieldLabel>Work category</FieldLabel>
													<FormControl size='small' fullWidth error={invalidFields.has('workCategory')}>
														<Select
															inputProps={{ 'aria-label': 'Work category' }}
															value={form.workCategory}
															onChange={(event) =>
																onGroupWorkCategoryChange(group.indices, event.target.value as WorkCategory)
															}>
															<MenuItem value='normal'>Normal</MenuItem>
															<MenuItem value='free_redo'>Free redo</MenuItem>
															<MenuItem value='rectify'>Rectify</MenuItem>
															<MenuItem value='fabrication'>Fabrication</MenuItem>
														</Select>
														{fieldMessage('workCategory') && (
															<FormHelperText>{fieldMessage('workCategory')}</FormHelperText>
														)}
													</FormControl>
												</Box>

												{projectHasNoPrecincts ? (
													<Box
														sx={{
															gridColumn: { md: 'span 9' },
															minHeight: 66,
															px: 1.5,
															py: 1,
															bgcolor: '#f8fafc',
															border: '1px solid #e2e8f0',
															borderRadius: 2,
															display: 'flex',
															alignItems: 'center',
															gap: 1,
														}}>
														<Chip label='Location optional' size='small' variant='outlined' />
														<Typography variant='body2' color='text.secondary'>
															No precincts are configured for this project. Location fields will be left blank.
														</Typography>
													</Box>
												) : (
													<>
														<Box sx={{ gridColumn: { md: 'span 3' }, ...compactControlSx }}>
															<FieldLabel>Precinct</FieldLabel>
															<FormControl
																size='small'
																fullWidth
																disabled={form.projectId == null}
																error={invalidFields.has('precinctId')}>
																<Select
																	displayEmpty
																	inputProps={{ 'aria-label': 'Precinct' }}
																	value={form.precinctId ?? ''}
																	renderValue={(value) =>
																		projectPrecincts.find((precinct) => precinct.id === Number(value))?.name ??
																		(value ? String(value) : placeholder('Select precinct'))
																	}
																	onChange={(event) =>
																		onGroupPrecinctChange(
																			group.indices,
																			event.target.value === '' ? null : Number(event.target.value)
																		)
																	}>
																	{form.precinctId != null &&
																		!projectPrecincts.some((precinct) => precinct.id === form.precinctId) && (
																			<MenuItem value={form.precinctId} sx={{ display: 'none' }}>
																				{form.precinctId}
																			</MenuItem>
																		)}
																	{projectPrecincts.map((precinct) => (
																		<MenuItem key={precinct.id} value={precinct.id}>
																			{precinct.name}
																		</MenuItem>
																	))}
																</Select>
																{fieldMessage('precinctId') && (
																	<FormHelperText>{fieldMessage('precinctId')}</FormHelperText>
																)}
															</FormControl>
														</Box>

														<Box sx={{ gridColumn: { md: 'span 2' }, ...compactControlSx }}>
															<FieldLabel>Block</FieldLabel>
															<FormControl
																size='small'
																fullWidth
																disabled={form.precinctId == null}
																error={invalidFields.has('blockId')}>
																<Select
																	displayEmpty
																	inputProps={{ 'aria-label': 'Block' }}
																	value={form.blockId ?? ''}
																	renderValue={(value) =>
																		blocksForPrecinct.find((block) => block.id === Number(value))?.number ??
																		(value ? String(value) : placeholder('Select block'))
																	}
																	onChange={(event) =>
																		onGroupBlockChange(
																			group.indices,
																			event.target.value === '' ? null : Number(event.target.value)
																		)
																	}>
																	{form.blockId != null &&
																		!blocksForPrecinct.some((block) => block.id === form.blockId) && (
																			<MenuItem value={form.blockId} sx={{ display: 'none' }}>
																				{form.blockId}
																			</MenuItem>
																		)}
																	{blocksForPrecinct.map((block) => (
																		<MenuItem key={block.id} value={block.id}>
																			{block.number}
																		</MenuItem>
																	))}
																</Select>
																{fieldMessage('blockId') && <FormHelperText>{fieldMessage('blockId')}</FormHelperText>}
															</FormControl>
														</Box>

														<Box sx={{ gridColumn: { md: 'span 2' }, ...compactControlSx }}>
															<FieldLabel htmlFor={`level-${form.cellId}`}>Level</FieldLabel>
															<TextField
																fullWidth
																id={`level-${form.cellId}`}
																size='small'
																type='number'
																value={form.level ?? ''}
																error={levelInvalid || invalidFields.has('level')}
																helperText={
																	fieldMessage('level') ??
																	(levelInvalid && levelTo !== null ? `Use ${levelFrom}-${levelTo}` : undefined)
																}
																onChange={(event) =>
																	updateCellForms(group.indices, {
																		level: event.target.value === '' ? null : Number(event.target.value),
																	})
																}
															/>
														</Box>

														<Box sx={{ gridColumn: { md: 'span 2' }, ...compactControlSx }}>
															<FieldLabel htmlFor={`units-${form.cellId}`}>
																{group.indices.length > 1 ? 'Units' : 'Unit'}
															</FieldLabel>
															<TextField
																fullWidth
																id={`units-${form.cellId}`}
																size='small'
																defaultValue={formatUnitList(forms, group.indices)}
																key={`${group.indices.map((index) => forms[index].cellId).join('|')}-units`}
																error={unitInvalid || invalidFields.has('unit')}
																helperText={
																	fieldMessage('unit') ?? (unitInvalid ? 'Check the block unit range' : undefined)
																}
																onBlur={(event) => updateGroupUnits(group.indices, event.target.value)}
															/>
														</Box>
													</>
												)}
												<Box
													sx={{
														gridColumn: { md: 'span 3' },
														minWidth: 0,
														display: 'grid',
														gridTemplateColumns: 'minmax(90px, 0.85fr) minmax(130px, 1.15fr)',
														gap: 1,
														...compactControlSx,
													}}>
													<Box>
														<FieldLabel htmlFor={`quantity-${form.cellId}`}>Quantity</FieldLabel>
														<TextField
															fullWidth
															id={`quantity-${form.cellId}`}
															size='small'
															type='number'
															inputProps={{ min: 0.01, step: 'any' }}
															value={quantityIsEditable ? form.quantity ?? '' : displayedQuantity}
															InputProps={{ readOnly: !quantityIsEditable }}
															error={quantityIsEditable && invalidFields.has('quantity')}
															helperText={quantityIsEditable ? fieldMessage('quantity') : undefined}
															onChange={(event) =>
																quantityIsEditable &&
																updateCellForms(group.indices, {
																	quantity: event.target.value === '' ? null : Number(event.target.value),
																})
															}
														/>
													</Box>
													<Box>
														<FieldLabel htmlFor={`quantity-unit-${form.cellId}`}>Quantity unit</FieldLabel>
														<TextField
															fullWidth
															id={`quantity-unit-${form.cellId}`}
															size='small'
															value={form.quantityUnit}
															inputProps={{ 'aria-label': 'Quantity unit' }}
															error={invalidFields.has('quantityUnit')}
															helperText={fieldMessage('quantityUnit')}
															onChange={(event) => updateCellForms(group.indices, { quantityUnit: event.target.value })}
														/>
													</Box>
												</Box>
											</Box>

											{projectTypeMissing && (
												<Alert severity='error' sx={{ mt: 1.25 }}>
													This activity is not set up for the selected project. Choose another activity or update the
													project catalogue.
												</Alert>
											)}
											{included && !complete && !projectTypeMissing && (
												<Typography variant='caption' color='warning.main' display='block' sx={{ mt: 1 }}>
													Please correct: {reviewIssues.join(' · ')}
												</Typography>
											)}
										</Box>
									);
								})}
							</Box>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ minHeight: 68, px: { xs: 2, md: 3 }, bgcolor: '#fff', borderTop: '1px solid #e2e8f0' }}>
				<Box sx={{ flex: 1, pl: 2 }}>
					{!readOnly && isWorkDone && incompleteCount > 0 && (
						<Typography variant='caption' color='warning.main'>
							Check the missing details before approving.
						</Typography>
					)}
				</Box>
				{readOnly ? (
					<Button variant='contained' onClick={onClose}>
						Close
					</Button>
				) : !isWorkDone ? (
					<>
						<Button variant='outlined' color='error' onClick={handleMarkReviewed} disabled={submitting}>
							{submitting ? <CircularProgress size={20} /> : 'Mark as not work'}
						</Button>
					</>
				) : (
					<Button
						variant='contained'
						onClick={handleSubmit}
						disabled={submitting || incompleteCount > 0 || (completeCount === 0 && rejectedCount === 0)}>
						{submitting ? (
							<CircularProgress size={20} />
						) : includedForms.length === 0 ? (
							'Finish review'
						) : (
							`Create ${completeCount} Work Done ${completeCount === 1 ? 'record' : 'records'}`
						)}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

const categoryLabel: Record<WorkCategory, string> = {
	normal: 'Normal',
	free_redo: 'Free redo',
	rectify: 'Rectify',
	fabrication: 'Fabrication',
};

const getProcessedWorker = (cell: ExtractionLogCell) => {
	const primary =
		cell.participants?.find((participant) => participant.role === 'primary')?.name ??
		cell.labels.senderName ??
		'Not recorded';
	const helpers = (cell.participants
		?.filter((participant) => participant.role === 'helper')
		.map((participant) => participant.name)
		.filter(Boolean) ??
		cell.coWorkers?.map((worker) => worker.name) ??
		[]) as string[];
	return { primary, helpers };
};

const getProcessedCategory = (cell: ExtractionLogCell): WorkCategory =>
	cell.workCategory ??
	cell.resolved.workCategory?.value ??
	(cell.resolved.statusType.value === 1 ? 'free_redo' : 'normal');

const getStructuredLocation = (cell: ExtractionLogCell): string => {
	const block = cell.labels.blockNumber;
	const level = cell.resolved.level.value;
	const unit = cell.resolved.unit.value;
	const hasStructuredLocation = block != null && level != null && unit != null;
	if (!hasStructuredLocation) return cell.reportedLocationText?.trim() || 'Area not recorded';
	return [cell.labels.precinctName, `Block ${block}`, `Level ${level}`, `Unit ${unit}`].filter(Boolean).join(' · ');
};

const ProcessedWorkSummary: React.FC<{ cells: ExtractionLogCell[] }> = ({ cells }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const groups = cells.reduce<Array<{ key: string; cells: ExtractionLogCell[] }>>((result, cell) => {
		const { primary, helpers } = getProcessedWorker(cell);
		const workCategory = getProcessedCategory(cell);
		const isClientBillable =
			cell.isClientBillable ??
			cell.resolved.isClientBillable?.value ??
			CATEGORY_DEFAULTS[workCategory].isClientBillable;
		const key = JSON.stringify([
			cell.labels.projectName,
			cell.labels.clientName,
			cell.labels.typeName,
			cell.labels.subTypeName,
			cell.labels.subTypeValue,
			primary,
			helpers,
			workCategory,
			isClientBillable,
		]);
		const existing = result.find((group) => group.key === key);
		if (existing) existing.cells.push(cell);
		else result.push({ key, cells: [cell] });
		return result;
	}, []);
	const selectedIndex = Math.min(activeIndex, Math.max(groups.length - 1, 0));
	const selectedGroup = groups[selectedIndex];

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: { xs: '1fr', md: groups.length > 1 ? '240px minmax(0, 1fr)' : '1fr' },
				gap: 1.5,
				alignItems: 'start',
			}}>
			{groups.length > 1 && (
				<Box
					component='nav'
					sx={{
						p: 1.25,
						minWidth: 0,
						bgcolor: '#fff',
						border: '1px solid #e2e8f0',
						borderRadius: 3,
						boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
					}}>
					<Box sx={{ px: 0.5 }}>
						<Typography variant='subtitle2' sx={{ fontWeight: 750 }}>
							{groups.length} recorded {groups.length === 1 ? 'item' : 'items'}
						</Typography>
						<Typography variant='caption' color='text.secondary'>
							Completed work details
						</Typography>
					</Box>
					<List disablePadding sx={{ mt: 1.25 }}>
						{groups.map((group, index) => {
							const first = group.cells[0];
							const title = [first.labels.typeName, first.labels.subTypeName, first.labels.subTypeValue]
								.filter(Boolean)
								.join(' · ');
							return (
								<ListItemButton
									key={group.key}
									selected={index === selectedIndex}
									onClick={() => setActiveIndex(index)}
									sx={{
										width: '100%',
										mb: 0.75,
										px: 1.25,
										py: 0.7,
										borderRadius: 2,
										border: '1px solid',
										borderColor: index === selectedIndex ? '#9fc0e9' : '#dce3eb',
										bgcolor: '#fff',
										'&.Mui-selected': { bgcolor: '#eef5ff' },
										'&.Mui-selected:hover': { bgcolor: '#e7f1ff' },
									}}>
									<Box sx={{ minWidth: 0 }}>
										<Typography variant='body2' sx={{ fontWeight: 650 }} noWrap>
											{title || `Work item ${index + 1}`}
										</Typography>
										<Typography variant='caption' color='text.secondary'>
											{group.cells.length} {group.cells.length === 1 ? 'unit' : 'units'}
										</Typography>
									</Box>
								</ListItemButton>
							);
						})}
					</List>
				</Box>
			)}
			<Box sx={{ minWidth: 0 }}>{selectedGroup && <ProcessedWorkGroup cells={selectedGroup.cells} />}</Box>
		</Box>
	);
};

const ProcessedWorkGroup: React.FC<{ cells: ExtractionLogCell[] }> = ({ cells }) => {
	const first = cells[0];
	const { primary, helpers } = getProcessedWorker(first);
	const work = [first.labels.typeName, first.labels.subTypeName, first.labels.subTypeValue].filter(Boolean).join(' · ');
	const workCategory = getProcessedCategory(first);
	const worker = [primary, ...helpers].join(', ');

	return (
		<Card
			variant='outlined'
			sx={{
				borderRadius: 3,
				borderColor: '#e2e8f0',
				boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
			}}>
			<CardContent sx={{ p: { xs: 1.5, md: 2.25 }, '&:last-child': { pb: { xs: 1.5, md: 2.25 } } }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' spacing={1}>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant='caption' sx={{ color: '#315f96', fontWeight: 700 }}>
							Recorded work
						</Typography>
						<Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
							{work || 'Work not recorded'}
						</Typography>
						<Typography variant='body2' color='text.secondary'>
							{[first.labels.projectName, `Workers: ${worker}`].filter(Boolean).join(' · ')}
						</Typography>
					</Box>
					<Stack direction='row' spacing={0.75} sx={{ flexShrink: 0 }}>
						{workCategory !== 'normal' && <Chip label={categoryLabel[workCategory]} size='small' variant='outlined' />}
						<Chip label='Completed' size='small' color='success' variant='outlined' />
					</Stack>
				</Stack>

				<Box
					sx={{
						mt: 1.5,
						pt: 1.5,
						borderTop: '1px solid',
						borderColor: 'divider',
						display: 'grid',
						gridTemplateColumns: cells.length > 1 ? 'repeat(auto-fit, minmax(280px, 1fr))' : 'minmax(0, 1fr)',
						gap: 1,
					}}>
					{cells.map((cell) => {
						const quantity = cell.quantity ?? cell.resolved.quantity?.value ?? 1;
						const quantityUnit = cell.quantityUnit ?? cell.resolved.quantityUnit?.value ?? 'unit';
						const hasExactUnit =
							cell.labels.blockNumber != null && cell.resolved.level.value != null && cell.resolved.unit.value != null;
						const showQuantity = !hasExactUnit || Number(quantity) !== 1;
						return (
							<Stack
								key={cell.cellId}
								direction='row'
								justifyContent='space-between'
								alignItems='center'
								spacing={1}
								sx={{ px: 1.25, py: 1, bgcolor: '#f8fafc', border: '1px solid #e6ebf1', borderRadius: 2 }}>
								<Typography variant='body2'>{getStructuredLocation(cell)}</Typography>
								{showQuantity && (
									<Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
										{quantity} {quantityUnit}
									</Typography>
								)}
							</Stack>
						);
					})}
				</Box>
			</CardContent>
		</Card>
	);
};
